import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type DealBreakdownResult, type WinLossResult } from '@whinerp/client';
import { formatMoney, formatPercent, todayIn } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Closed deals by segment (G-207, G-212) and why they were won or lost
 * (G-209), under the funnel on the board.
 */

const DIMENSIONS = ['owner', 'source', 'industry', 'segment', 'size'] as const;
const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function DealAnalysisPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = can('analytics.view');
  const [dimension, setDimension] = useState<(typeof DIMENSIONS)[number]>('source');
  const [days, setDays] = useState(180);
  const to = todayIn(format);
  const from = new Date(Date.parse(to) - days * 86_400_000).toISOString().slice(0, 10);
  const breakdown = useQuery<DealBreakdownResult, ApiError>({
    queryKey: ['deal-breakdown', dimension, from, to],
    queryFn: () => api.dealBreakdown({ query: { from, to, dimension } }),
    enabled,
    placeholderData: (previous) => previous,
  });
  const winLoss = useQuery<WinLossResult, ApiError>({ queryKey: ['win-loss', from, to], queryFn: () => api.winLoss({ query: { from, to } }), enabled });
  if (!enabled || !breakdown.data || !winLoss.data) return null;
  const money = (amount: string | null) => (amount === null ? '-' : formatMoney(amount, format, { currency: breakdown.data.currency }));
  return (
    <Panel data-region="deal-analysis">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('dealAnalysis.title')}</SectionLabel>
          <select className={`${INPUT} ms-auto`} data-analysis-dimension aria-label={t('dealAnalysis.by')} value={dimension} onChange={(event) => setDimension(event.target.value as 'source')}>
            {DIMENSIONS.map((key) => <option key={key} value={key}>{t(`dealAnalysis.dimension.${key}`)}</option>)}
          </select>
          <select className={INPUT} data-analysis-days aria-label={t('dealAnalysis.period')} value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {[90, 180, 365].map((value) => <option key={value} value={value}>{t('dealAnalysis.lastDays', { values: { days: String(value) } })}</option>)}
          </select>
        </div>
        <div data-scroll-container className="overflow-x-auto">
          <table className="w-full text-dense" data-analysis-table>
            <thead>
              <tr className="text-meta text-ink-meta">
                <th className="p-1 text-start font-normal">{t(`dealAnalysis.dimension.${dimension}`)}</th>
                <th className="p-1 text-end font-normal">{t('dealAnalysis.closed')}</th>
                <th className="p-1 text-end font-normal">{t('dealAnalysis.winRate')}</th>
                <th className="p-1 text-end font-normal">{t('dealAnalysis.wonValue')}</th>
                <th className="p-1 text-end font-normal">{t('dealAnalysis.avgSize')}</th>
                <th className="p-1 text-end font-normal">{t('dealAnalysis.daysToWin')}</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.data.rows.map((row) => (
                <tr key={row.key} data-analysis-row={row.key} data-small={row.small} className={row.small ? 'text-ink-meta' : 'text-ink'}>
                  <td className="p-1">{row.key}{row.small ? ` ${t('dealAnalysis.few')}` : ''}</td>
                  <td className="p-1 text-end">{row.closed}</td>
                  <td className="p-1 text-end" data-analysis-win-rate>{row.winRate === null ? '-' : formatPercent(row.winRate, format)}</td>
                  <td className="p-1 text-end">{money(row.wonValue)}</td>
                  <td className="p-1 text-end">{money(row.averageWonSize)}</td>
                  <td className="p-1 text-end">{row.averageDaysToWin ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid gap-3 sm:grid-cols-2" data-win-loss>
          <div className="flex flex-col gap-1">
            <p className="text-meta text-ink-meta">{t('dealAnalysis.lossReasons', { values: { lost: String(winLoss.data.lost) } })}</p>
            <ul className="flex flex-col text-dense">
              {winLoss.data.lossReasons.map((row) => <li key={row.reason} data-loss-reason={row.reason} className="flex"><span>{row.reason}</span><span className="ms-auto">{row.count}</span></li>)}
            </ul>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-meta text-ink-meta">{t('dealAnalysis.competitors')}</p>
            <ul className="flex flex-col text-dense">
              {winLoss.data.competitors.map((row) => (
                <li key={row.name} data-competitor-record={row.name} className="flex">
                  <span>{row.name}</span>
                  <span className="ms-auto">{t('dealAnalysis.record', { values: { won: String(row.wonAgainst), lost: String(row.lostTo) } })}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Panel>
  );
}
